import React, { useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SportsContext } from '../../_context/SportsContext';
import { SportsnewProvider } from '../../_context/SportsnewContext';
import { formatOdds, formatDelta } from './_helpers/oddsFormat';
import { formatSportsMatchTimeKst } from './_helpers/sportsMatchTime';
import { SettingContext } from '../../_context/setting.contax';
import { SportsBetCartContext } from '../../_context/SportsBetCartContext';
import useServiceContextHook from '../../_hooks/service.context.hook';
import FigmaSportsShell from './figma/FigmaSportsShell';
import { buildMarketDisplayTitle } from './figma/marketTitleResolver';
import { getSelectionLabel } from './figma/selectionLabelResolver';
import SportsLive from './SportsLive';
import { getCustomerMarketKey, getSourceMarketId } from '../../_helpers/inplayMarketIdentity';
import './figma/figma-sports.css';
import TeamLogo from './TeamLogo';

/** HC 계열 (Asian / European / Spread) 감지 — 사용자 확정 규칙 (2026-04-08).
 *  HC-only base_line set 규칙. cart market_line 분기에 사용. */
const _isHCMarketSpMain = (mkt) => {
  if (!mkt) return false;
  const code = String(mkt.market_code || mkt.market_type?.code || '');
  if (/^FEED_\d+_(3|13|53|61|64|65|66|67|95|250|281|342|407|433|550|935|1083|1248|1558|1693|1694|2286|2732)$/.test(code)) return true;
  const name = String(buildMarketDisplayTitle(mkt) || mkt.market_name || mkt.market_type_name || mkt.market_type?.name || mkt.name || '');
  return /핸디캡|handicap|spread/i.test(name);
};

/** 종목 아이콘 매핑 */
const SPORT_ICONS = {
  football: '\u26BD', soccer: '\u26BD', '축구': '\u26BD',
  basketball: '\uD83C\uDFC0', '농구': '\uD83C\uDFC0',
  baseball: '\u26BE', '야구': '\u26BE',
  tennis: '\uD83C\uDFBE', '테니스': '\uD83C\uDFBE',
  volleyball: '\uD83C\uDFD0', '배구': '\uD83C\uDFD0',
  hockey: '\uD83C\uDFD2', '하키': '\uD83C\uDFD2',
  esports: '\uD83C\uDFAE', '이스포츠': '\uD83C\uDFAE',
  mma: '\uD83E\uDD4A', boxing: '\uD83E\uDD4A',
  default: '\uD83C\uDFC6'
};

const getSportIcon = (sport) => {
  if (!sport) return SPORT_ICONS.default;
  /** DB icon_url 우선 */
  if (sport.icon_url && sport.icon_url.length <= 4) return sport.icon_url;
  const key = (sport.slug || sport.name || '').toLowerCase();
  return SPORT_ICONS[key] || SPORT_ICONS[(sport.name_ko || '')] || SPORT_ICONS.default;
};

/** 베팅 타입 탭 정의 */
const BETTING_TYPES = [
  { key: 'DOMESTIC', label: '국내형' },
  { key: 'INTERNATIONAL', label: '해외형' },
  { key: 'SPECIAL', label: '스페셜' },
  { key: 'LIVE', label: '라이브' },
];

/** fix90: 다폴더 보너스 — API에서 동적 로딩 */

const SportsMain = () => {
  const {
    categories, selectedSport, setSelectedSport, matches, loading, pageLoading, oddsFlash, oddsLatest, marketLatest,
    bettingType, setBettingType, searchQuery, setSearchQuery, sortOrder, setSortOrder,
    liveCount, matchCounts, displaySettings,
    goToPage, currentPage, totalPages, hasMoreMatches, hasPrevPage, matchTotal,
    matchTotalScopeKey, currentMatchTotalScopeKey,
    primeOddsLatest, popularMode, setPopularMode,
  } = useContext(SportsContext);

  const _fmt = displaySettings?.default_odds_format || 'decimal';
  const { toggleSelection, isSelected, selections } = useContext(SportsBetCartContext);
  const { services: { SportsService } } = useServiceContextHook();
  const { sportsEnabled } = useContext(SettingContext);
  const navigate = useNavigate();

  /** Combo bonus is fetched by FigmaBetCart via SportsnewService — no duplicate call here */
  const [bonusTiers] = useState([]);
  const [bonusBuckets] = useState({});

  /** bettingType -> bucket name 매핑 */
  const BETTING_TYPE_TO_BUCKET = { DOMESTIC: "domestic", INTERNATIONAL: "international", SPECIAL: "special", LIVE: "live" };

  /** 현재 탭 기준으로 실제 표시할 bonus tiers 계산 */
  const effectiveBonusTiers = useMemo(() => {
    const bucket = BETTING_TYPE_TO_BUCKET[bettingType];
    if (!bucket) return bonusTiers;
    const bucketTiers = bonusBuckets[bucket];
    if (bucketTiers && bucketTiers.length > 0) return bucketTiers;
    return bonusTiers;
  }, [bettingType, bonusTiers, bonusBuckets]);

  /** 현재 적용되는 bonus fold (최고 1개만) */
  const activeBonusFold = useMemo(() => {
    if (selections.length < 3 || !effectiveBonusTiers?.length) return null;
    const applicable = effectiveBonusTiers.filter(t => selections.length >= t.fold).sort((a, b) => b.fold - a.fold);
    return applicable[0]?.fold || null;
  }, [selections.length, effectiveBonusTiers]);

  /** fix75-6: 스포츠 OFF 시 메인으로 리다이렉트 */
  useEffect(() => {
    if (sportsEnabled === false) navigate('/', { replace: true });
  }, [sportsEnabled, navigate]);

  const isDomestic = bettingType === 'DOMESTIC';
  const isLive = bettingType === 'LIVE';
  const usesSideDetailPanel = !isDomestic && !isLive;
  const preferGenericTeamLabels = bettingType === 'DOMESTIC' || bettingType === 'SPECIAL';

  /** 3-panel: 선택된 경기 상세 */
  const [selectedMatchId, setSelectedMatchId] = useState(null);

  const [matchDetail, setMatchDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [collapsedMarkets, setCollapsedMarkets] = useState({});

  // 국내형 서브마켓 펼침: 이제 항상 표시되므로 expand state 불필요

  /** 상세 API race guard — 최신 요청만 반영 */
  const detailSeqRef = useRef(0);

  /** 경기 상세 로드 — 현재 탭 betting_category 전달 */
  const loadMatchDetail = useCallback(async (matchId) => {
    if (!matchId) { setMatchDetail(null); return; }
    const seq = ++detailSeqRef.current;
    setDetailLoading(true);
    try {
      const res = await SportsService.getMatchDetail(matchId, { betting_category: bettingType });
      if (seq !== detailSeqRef.current) return;
      if (res?.status) {
        setMatchDetail(res.data);
        // detail 재로드 시에는 stale oddsLatest를 fresh API 값으로 덮어쓴다.
        // (overwrite=false 기본은 신규 entry만 추가해서 INTERNATIONAL OU stale 가격이 잔존하는 문제가 있었음)
        primeOddsLatest?.(res.data?.markets, { overwrite: true });
      }
    } catch (e) {
      if (seq === detailSeqRef.current) console.error('상세 로드 실패:', e);
    } finally {
      if (seq === detailSeqRef.current) setDetailLoading(false);
    }
  }, [SportsService, bettingType, primeOddsLatest]);

  /** 현재 목록에 실제로 남아 있는 선택 경기만 detail target으로 인정한다. */
  const selectedVisibleMatchId = useMemo(() => {
    if (!usesSideDetailPanel || !selectedMatchId) return null;
    return matches.some((match) => Number(match?.id) === Number(selectedMatchId))
      ? selectedMatchId
      : null;
  }, [matches, selectedMatchId, usesSideDetailPanel]);

  /** 해외형/스페셜 활성 경기 ID: LIVE 상세는 SportsLive가 전담한다. */
  const detailTargetId = usesSideDetailPanel ? (selectedVisibleMatchId || matches[0]?.id || null) : null;

  useEffect(() => {
    if (detailTargetId) loadMatchDetail(detailTargetId);
    else setMatchDetail(null);
  }, [detailTargetId, loadMatchDetail]);

  useEffect(() => {
    if (!usesSideDetailPanel || !selectedMatchId || selectedVisibleMatchId) return;
    setSelectedMatchId(null);
    setCollapsedMarkets({});
  }, [selectedMatchId, selectedVisibleMatchId, usesSideDetailPanel]);

  /** 베팅 타입 변경시 상세 초기화 */
  useEffect(() => {
    setSelectedMatchId(null);
    setMatchDetail(null);
    setCollapsedMarkets({});
  }, [bettingType]);

  /** 날짜/시간 포맷 — fixed Asia/Seoul, never the browser's local timezone */
  const formatTime = formatSportsMatchTimeKst;

  /** 종목별 경기 수 */
  const liveRowCounts = useMemo(() => {
    const bySport = new Map();
    let total = 0;
    if (bettingType !== 'LIVE') return { bySport, total };
    (matches || []).forEach((match) => {
      if (!(match?.is_live || match?.status === 'LIVE')) return;
      total += 1;
      const rowSportId = Number(match?.sport_id ?? match?.sport?.id);
      if (Number.isFinite(rowSportId)) {
        bySport.set(rowSportId, (bySport.get(rowSportId) || 0) + 1);
      }
    });
    return { bySport, total };
  }, [bettingType, matches]);

  const [matchCountsCache, setMatchCountsCache] = useState({});

  useEffect(() => {
    const countType = matchCounts?._betting_category;
    if (!countType || !Array.isArray(matchCounts?.counts)) return;
    setMatchCountsCache(prev => (
      prev[countType] === matchCounts ? prev : { ...prev, [countType]: matchCounts }
    ));
  }, [matchCounts]);

  const currentMatchCounts = useMemo(() => {
    if (matchCounts?._betting_category && matchCounts._betting_category !== bettingType) {
      return matchCountsCache[bettingType] || { counts: [], total: 0 };
    }
    return matchCounts || matchCountsCache[bettingType] || { counts: [], total: 0 };
  }, [bettingType, matchCounts, matchCountsCache]);

  const totalCount = useMemo(() => {
    const apiTotal = Number(currentMatchCounts?.total || 0);
    if (bettingType !== 'LIVE') return apiTotal;
    const scopedTotal = matchTotalScopeKey === currentMatchTotalScopeKey ? Number(matchTotal || 0) : 0;
    return apiTotal
      || Number(liveCount || 0)
      || liveRowCounts.total
      || scopedTotal;
  }, [bettingType, currentMatchCounts?.total, currentMatchTotalScopeKey, liveCount, liveRowCounts.total, matchTotal, matchTotalScopeKey]);

  const getCountForSport = useCallback((sportId) => {
    if (!currentMatchCounts?.counts) return bettingType === 'LIVE' ? (liveRowCounts.bySport.get(Number(sportId)) || 0) : 0;
    const found = currentMatchCounts.counts.find(c => Number(c.sport_id) === Number(sportId));
    const apiCount = found ? Number(found.count) : 0;
    if (bettingType !== 'LIVE') return apiCount;
    const rowCount = liveRowCounts.bySport.get(Number(sportId)) || 0;
    const isActiveSport = Number(selectedSport?.id) === Number(sportId);
    if (isActiveSport) {
      const hasCurrentScopedTotal = matchTotalScopeKey === currentMatchTotalScopeKey;
      if (hasCurrentScopedTotal) return Number(matchTotal || 0);
      if (loading && rowCount === 0) return apiCount;
      return rowCount || apiCount;
    }
    return rowCount || apiCount;
  }, [bettingType, currentMatchCounts?.counts, currentMatchTotalScopeKey, liveRowCounts.bySport, loading, matchTotal, matchTotalScopeKey, selectedSport?.id]);

  /** 리그별 그룹핑 (빈 마켓 경기 제외) */
  /** API 순서(시간→국가→리그) 보존 그룹핑: 연속된 같은 리그만 묶음 */
  const groupedByLeague = useMemo(() => {
    const result = [];
    let lastKey = null;
    let currentGroup = null;
    (matches || []).forEach(m => {
      if (!m.markets || m.markets.length === 0) return;
      const leagueKey = m.league?.id || 'unknown';
      if (leagueKey !== lastKey) {
        currentGroup = {
          league: m.league || { name: '기타', name_ko: '기타' },
          sport: m.sport || {},
          matches: []
        };
        result.push(currentGroup);
        lastKey = leagueKey;
      }
      currentGroup.matches.push(m);
    });
    return result;
  }, [matches]);

  /** 마켓 추출 — backend contract 기준 (market_id로 분리) */
  const getMarkets = (match) => {
    const mkts = match.markets || [];
    if (mkts.length === 0) return { main: null, handicap: null, overunder: null, allMarkets: [] };
    let main = match.main_market || null;
    // 축구 1X2: odds가 1/X/2 모두 있어야 유효한 main
    if (main && Number(match.sport_id) === 1) {
      const mainOddsList = main.odds || [];
      const names = mainOddsList.map(o => o.name);
      if (!names.includes('1') || !names.includes('X') || !names.includes('2')) main = null;
    }
    const mainId = main?.market_id || main?.id;
    // market_id 기준으로 main과 분리 (객체 참조 비교 금지)
    // handicap_pair는 backend가 normalized pair로 제공
    const handicap = match.handicap_pair || null;
    // OU는 markets에서 HC 아닌 non-main
    // OU: FEED_1_2(Under/Over) 또는 FEED_2_28(U/O Including OT) 명시 매칭
    const overunder = mkts.find(mk => ['FEED_1_2','FEED_2_28','FEED_3_2','FEED_3_28','FEED_4_2','FEED_5_2','FEED_5_28','FEED_5_551'].includes(mk.market_code));
    return { main, handicap, overunder, allMarkets: mkts };
  };

  /** 배당 클릭 */
  const handleOddsClick = (match, market, oddsItem) => {
    // market_group_key: 같은 경기의 같은 logical market = 같은 key
    const mgk = market.home_market_id
      ? `${match.id}:HC:${market.home_market_id}:${market.away_market_id}`
      : `${match.id}:${getCustomerMarketKey(market) || market.id || market.market_id}`;
    // 클릭한 selection 기준으로 정확한 market_id 결정
    // - DOMESTIC normalized handicap_pair: home_market_id / away_market_id 사용
    // - INTERNATIONAL/기타 merged detail market: 같은 market 객체 안에 서로 다른 source market_id를 가진
    //   odds가 섞여 있을 수 있음 (예: HC ±line pair). odds 자신의 market_id를 최우선으로 사용해야
    //   서버 validation의 `배당과 마켓이 일치하지 않습니다` 에러를 방지할 수 있음
    let resolvedMarketId;
    if (market.home_selection && market.away_selection) {
      if (oddsItem.id === market.away_selection.id) resolvedMarketId = market.away_market_id;
      else resolvedMarketId = market.home_market_id;
    } else {
      resolvedMarketId = getSourceMarketId(oddsItem, market) ?? oddsItem.market_id ?? market.source_market_id ?? market.market_id ?? market.id;
    }
    const marketDisplayKey = getCustomerMarketKey(oddsItem, market);
    // suspend pre-check: customer market 기준 (marketLatest 우선, fallback _sourceMarketSuspended)
    const _oddsSelfSus = oddsLatest[oddsItem.id]?.is_suspended ?? oddsItem.is_suspended;
    const _srcMktSus = marketLatest?.[marketDisplayKey]?.is_suspended ?? oddsItem._sourceMarketSuspended ?? false;
    if (_oddsSelfSus || _srcMktSus) return;
    const selectionNameRaw = oddsItem.name || oddsItem.name_ko || '';
    toggleSelection({
      match_id: match.id,
      market_id: resolvedMarketId,
      source_market_id: resolvedMarketId,
      customer_market_key: marketDisplayKey,
      market_group_key: mgk,
      odds_id: oddsItem.id,
      match_name: `${match.home_team_ko || match.home_team} vs ${match.away_team_ko || match.away_team}`,
      market_name: buildMarketDisplayTitle(market) || market.market_name || market.name || market.market_code || '마켓',
      market_title_display: buildMarketDisplayTitle(market),
      market_type_name: buildMarketDisplayTitle(market) || market.market_name || market.market_type_name || market.market_code || '',
      // 사용자 확정 규칙 (2026-04-08):
      //  - HC 계열: exact base_line (FigmaInternationalBoard.normalizeMarkets 가 _baseLineLabel inject)
      //  - 비-HC: 기존 fallback (display_line / base_line / line / home_line)
      market_line: (market._isHCSet || _isHCMarketSpMain(market))
        ? (market._baseLineLabel || market.base_line || '')
        : (market.display_line || market.base_line || market.line || market.home_line || ''),
      selection_name: selectionNameRaw,
      selection_name_display: getSelectionLabel(oddsItem, {
        market,
        match,
        fallback: oddsItem.name_ko || selectionNameRaw || '-',
        preferGenericTeamLabels,
      }),
      home_team: match.home_team,
      away_team: match.away_team,
      home_team_ko: match.home_team_ko,
      away_team_ko: match.away_team_ko,
      odds: oddsLatest[oddsItem.id]?.odds ?? oddsItem.odds,
    });
  };

  /** 경기 클릭 → 상세 패널 */
  const handleMatchClick = (matchId) => {
    if (isDomestic) return;
    setSelectedMatchId(prev => prev === matchId ? null : matchId);
  };

  const handleSelectedSportChange = useCallback((sport, options = {}) => {
    if (!options.preservePopularMode) {
      setPopularMode?.(false);
    }

    if (bettingType !== 'LIVE') {
      setSelectedSport(sport);
      return;
    }

    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : '',
    );
    params.set('type', 'LIVE');
    params.set('page', '1');
    params.set('live_page', '1');
    if (sport?.id) params.set('sport_id', String(sport.id));
    else params.delete('sport_id');
    navigate(`/sports?${params.toString()}`, { replace: true });
  }, [bettingType, navigate, setPopularMode, setSelectedSport]);

  /** 마켓 접기/펼치기 */
  const toggleMarketCollapse = (mktId) => {
    setCollapsedMarkets(prev => ({ ...prev, [mktId]: !prev[mktId] }));
  };

  /** 배당 블록 버튼 */
  const OddsBlock = ({ match, market, odds, label, tag, arrowDir, className = '' }) => {
    if (!odds) {
      return (
        <div className={`sp_ob ${className} sp_ob_empty`}>
          <span className="sp_ob_name">{label || '-'}</span>
          <span className="sp_ob_val">-</span>
        </div>
      );
    }
    // odds:bulk_update / market:suspend 직후 즉시 반영을 위해 oddsLatest merge 우선
    const sus = oddsLatest[odds.id]?.is_suspended ?? odds.is_suspended;
    const dispOdds = oddsLatest[odds.id]?.odds ?? odds.odds;
    const flash = oddsFlash[odds.id];
    const displayName = label || odds.name_ko || odds.name;
    return (
      <button type="button"
        className={`sp_ob ${className} ${isSelected(odds.id) ? 'sp_ob_sel' : ''} ${sus ? 'sp_ob_sus' : ''} ${flash ? 'sp_ob_flash_' + flash.direction : ''}`}
        onClick={(e) => { e.stopPropagation(); !sus && handleOddsClick(match, market, odds); }}
        disabled={sus}>
        <span className="sp_ob_name">{displayName}</span>
        {tag && <span className="sp_ob_tag">{tag}</span>}
        {arrowDir === 'up' && <span className="sp_ob_arrow sp_ob_arrow_up">{'\u2191'}</span>}
        {arrowDir === 'down' && <span className="sp_ob_arrow sp_ob_arrow_dn">{'\u2193'}</span>}
        <span className="sp_ob_val">
          {formatOdds(dispOdds, _fmt)}
        </span>
        {flash && <span className={`sp_delta sp_delta_${flash.direction}`}>{flash.direction === 'up' ? '+' : '-'}{formatDelta(flash.delta, _fmt)}</span>}
      </button>
    );
  };

  if (sportsEnabled === false) return null;

  return (
    <SportsnewProvider>
    <FigmaSportsShell
      bettingType={bettingType}
      setBettingType={setBettingType}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      categories={categories}
      selectedSport={selectedSport}
      setSelectedSport={handleSelectedSportChange}
      totalCount={totalCount}
      getCountForSport={getCountForSport}
      getSportIcon={getSportIcon}
      liveCount={liveCount}
      matchCounts={matchCounts}
      effectiveBonusTiers={effectiveBonusTiers}
      activeBonusFold={activeBonusFold}
      groupedByLeague={groupedByLeague}
      loading={loading}
      pageLoading={pageLoading}
      matches={matches}
      isSelected={isSelected}
      handleOddsClick={handleOddsClick}
      oddsFlash={oddsFlash}
      oddsLatest={oddsLatest}
      marketLatest={marketLatest}
      formatOdds={formatOdds}
      formatDelta={formatDelta}
      formatTime={formatTime}
      _fmt={_fmt}
      selectedMatchId={selectedMatchId}
      handleMatchClick={handleMatchClick}
      matchDetail={matchDetail}
      isDomestic={isDomestic}
      goToPage={goToPage}
      currentPage={currentPage}
      totalPages={totalPages}
      hasMoreMatches={hasMoreMatches}
      hasPrevPage={hasPrevPage}
      matchTotal={matchTotal}
      getMarkets={getMarkets}
      OddsBlock={OddsBlock}
      popularMode={popularMode}
      setPopularMode={setPopularMode}
      SportsLiveComponent={SportsLive}
    />
    </SportsnewProvider>
  );
};

export default SportsMain;
